import type { TranslationKey } from "@veycue/i18n";
import { useState } from "react";
import { api } from "../api/client";
import type { Cue } from "../api/types";
import { CueList } from "../components/CueList";
import { EmptyState, ErrorState, Loading, Page } from "../components/states";
import { useApi } from "../hooks/useApi";
import { useSession } from "../state/session";

const FILTERS = [
  { id: "open", statuses: "new,viewed", label: "cue.status.new" },
  { id: "snoozed", statuses: "snoozed", label: "cue.status.snoozed" },
  { id: "resolved", statuses: "resolved", label: "cue.status.resolved" },
  { id: "ignored", statuses: "ignored,false_positive", label: "cue.status.ignored" },
] as const;

const ACTIONS = [
  { action: "resolve", label: "cue.action.resolve" },
  { action: "snooze", label: "cue.action.snooze" },
  { action: "not_relevant", label: "cue.action.notRelevant" },
  { action: "wrong", label: "cue.action.wrong" },
] as const;

export function Cues() {
  const { organization, translator } = useSession();
  const { t } = translator;

  const [filter, setFilter] = useState<(typeof FILTERS)[number]["id"]>("open");
  const [selected, setSelected] = useState<Cue | null>(null);
  const [busy, setBusy] = useState(false);

  const statuses = FILTERS.find((item) => item.id === filter)?.statuses ?? "new,viewed";
  const path = organization
    ? `/v1/organizations/${organization.id}/cues?status=${statuses}&limit=50`
    : null;
  const { data, error, loading, reload } = useApi<{ cues: Cue[] }>(path);

  async function sendFeedback(cue: Cue, action: string) {
    if (!organization) return;
    setBusy(true);
    try {
      await api(`/v1/organizations/${organization.id}/cues/${cue.id}/feedback`, {
        method: "POST",
        body: { action },
      });
      setSelected(null);
      reload();
    } finally {
      setBusy(false);
    }
  }

  const cues = data?.cues ?? [];

  return (
    <Page title={t("nav.attentionCenter")} lead={t("common.tagline")}>
      <div className="row" style={{ marginBottom: "var(--vc-space-xl)" }}>
        {FILTERS.map((item) => (
          <button
            key={item.id}
            type="button"
            className={`btn btn--small ${filter === item.id ? "btn--primary" : "btn--ghost"}`}
            onClick={() => setFilter(item.id)}
          >
            {t(item.label as TranslationKey)}
          </button>
        ))}
      </div>

      {error !== null && <ErrorState error={error} />}

      {loading ? (
        <Loading />
      ) : cues.length === 0 ? (
        <EmptyState title={t("cue.empty")} description={t("today.allClear")} />
      ) : (
        <CueList cues={cues} onSelect={setSelected} />
      )}

      {selected && (
        <section className="card" style={{ marginTop: "var(--vc-space-xl)" }}>
          <p className="card__label">{t("cue.recommendedAction")}</p>
          <p style={{ fontWeight: 600 }}>
            {t(selected.titleKey as TranslationKey, selected.titleParams ?? undefined)}
          </p>

          <div className="row" style={{ marginTop: "var(--vc-space-lg)" }}>
            {ACTIONS.map((item) => (
              <button
                key={item.action}
                type="button"
                className="btn btn--ghost btn--small"
                disabled={busy}
                onClick={() => void sendFeedback(selected, item.action)}
              >
                {t(item.label as TranslationKey)}
              </button>
            ))}
          </div>

          {/* Każdy sygnał niesie pewność analizy — §27 i §39 wymagają jej pokazania. */}
          <p className="ai-notice">{t("ask.aiNotice")}</p>
        </section>
      )}
    </Page>
  );
}
